import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { XMarkIcon, PlayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface Tool {
  name: string;
  description?: string;
  schema?: any;
}

interface ToolExecutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  tool: Tool;
  serverPath: string;
  onShowToast?: (message: string, type: 'success' | 'error') => void;
}

const ToolExecutionModal: React.FC<ToolExecutionModalProps> = ({
  isOpen,
  onClose,
  tool,
  serverPath,
  onShowToast
}) => {
  const [parameters, setParameters] = useState<Record<string, any>>({});
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Initialize parameters from schema
  useEffect(() => {
    if (tool.schema?.properties) {
      const initialParams: Record<string, any> = {};
      Object.keys(tool.schema.properties).forEach(key => {
        const prop = tool.schema.properties[key];
        // Set default value if available
        if (prop.default !== undefined) {
          initialParams[key] = prop.default;
        } else if (tool.schema.required?.includes(key)) {
          // Initialize required fields with empty values
          initialParams[key] = '';
        }
      });
      setParameters(initialParams);
    }
  }, [tool]);

  const handleExecute = async () => {
    setExecuting(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/servers${serverPath}/tools/${tool.name}`,
        parameters,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setResult(response.data);
      onShowToast?.('Tool executed successfully', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to execute tool';
      setError(errorMessage);
      onShowToast?.(errorMessage, 'error');
    } finally {
      setExecuting(false);
    }
  };

  const renderParameterInput = (paramName: string, paramSchema: any) => {
    const isRequired = tool.schema?.required?.includes(paramName);
    const value = parameters[paramName] ?? '';

    // Handle anyOf (union types like Optional[str])
    let actualType = paramSchema.type;
    let isOptional = false;

    if (paramSchema.anyOf) {
      // Find the non-null type
      const nonNullType = paramSchema.anyOf.find((t: any) => t.type !== 'null');
      if (nonNullType) {
        actualType = nonNullType.type;
        isOptional = paramSchema.anyOf.some((t: any) => t.type === 'null');
      }
    }

    const handleChange = (newValue: any) => {
      setParameters(prev => ({
        ...prev,
        [paramName]: newValue
      }));
    };

    // Boolean type - checkbox
    if (actualType === 'boolean') {
      return (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => handleChange(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {paramSchema.description || paramName}
          </span>
        </label>
      );
    }

    // Number type
    if (actualType === 'integer' || actualType === 'number') {
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => handleChange(e.target.value ? Number(e.target.value) : '')}
          required={isRequired && !isOptional}
          placeholder={paramSchema.description || `Enter ${paramName}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      );
    }

    // Object type - JSON editor
    if (actualType === 'object') {
      return (
        <textarea
          value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              handleChange(parsed);
            } catch {
              handleChange(e.target.value);
            }
          }}
          required={isRequired && !isOptional}
          placeholder={paramSchema.description || `Enter ${paramName} (JSON)`}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
        />
      );
    }

    // Enum/choices
    if (paramSchema.enum && Array.isArray(paramSchema.enum)) {
      return (
        <select
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          required={isRequired && !isOptional}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">Select {paramName}</option>
          {paramSchema.enum.map((option: string) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    // Default: string input
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        required={isRequired && !isOptional}
        placeholder={paramSchema.description || `Enter ${paramName}`}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
      />
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Execute Tool: {tool.name}
            </h2>
            {tool.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {tool.description}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Parameters Form */}
            {tool.schema?.properties && Object.keys(tool.schema.properties).length > 0 ? (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Parameters
                </h3>
                {Object.entries(tool.schema.properties).map(([paramName, paramSchema]: [string, any]) => {
                  const isRequired = tool.schema.required?.includes(paramName);
                  const isOptional = paramSchema.anyOf?.some((t: any) => t.type === 'null');

                  return (
                    <div key={paramName} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {paramName}
                        {isRequired && !isOptional && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                        {paramSchema.default !== undefined && (
                          <span className="text-gray-500 ml-2 font-normal">
                            (default: {JSON.stringify(paramSchema.default)})
                          </span>
                        )}
                      </label>
                      {renderParameterInput(paramName, paramSchema)}
                      {paramSchema.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {paramSchema.description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This tool does not require any parameters.
              </p>
            )}

            {/* Execute Button */}
            <button
              onClick={handleExecute}
              disabled={executing}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors"
            >
              {executing ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  Executing...
                </>
              ) : (
                <>
                  <PlayIcon className="h-5 w-5" />
                  Execute Tool
                </>
              )}
            </button>

            {/* Results */}
            {(result || error) && (
              <div className="space-y-2">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {error ? 'Error' : 'Result'}
                </h3>
                <div className={`p-4 rounded-lg ${
                  error
                    ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                    : 'bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700'
                }`}>
                  <pre className="text-sm text-gray-900 dark:text-gray-100 overflow-x-auto whitespace-pre-wrap break-words">
                    {error || JSON.stringify(result, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToolExecutionModal;
